import { fetchApi } from './api';

export type Tier = 'A' | 'B' | 'C';

export interface YearRow {
    ClassYear: number;
    SupplierLockedAt: string | null;
    SupplierLockedBy: string | null;
    CustomerLockedAt: string | null;
    CustomerLockedBy: string | null;
}

export interface LockStatus {
    supplierLocked: boolean;
    customerLocked: boolean;
    supplierLockedAt: string | null;
    customerLockedAt: string | null;
    supplierLockedBy: string | null;
    customerLockedBy: string | null;
}

export interface ClassificationConfig {
    classYear: number;
    supplierFinanceWeight: number;
    supplierWeightImportant: number;
    supplierWeightFamilyOwned: number;
    supplierWeightLocalSF: number;
    supplierWeightMoreCountries: number;
    supplierWeightBrand: number;
    supplierThresholdA: number;
    supplierThresholdB: number;
    supplierFinancePercentiles: number[];
    supplierCommissionMultiplier: number;
    customerGpMultiplier: number;
    customerASupMultiplier: number;
    customerProjMultiplier: number;
    customerPdMultiplier: number;
    customerThresholdA: number;
    customerThresholdB: number;
    customerGpPercentiles: number[];
    hubspotProjectStages: string[];
    hubspotProjectYearWindow: number;
    projectGpBands: number[];
    paymentDelayDaysThreshold: number;
}

export interface SupplierRow {
    CompanyCode: string;
    SupplierDimID: string;
    ClassYear: number;
    SupplierName: string | null;
    ActSalesGP: number;
    ActCommissionGP: number;
    BudSalesGP: number;
    BudCommissionGP: number;
    FinanceGpYear: number;
    FinancePoints: number;
    ScoreImportant: number | null;
    ScoreFamilyOwned: number | null;
    ScoreLocalSF: number | null;
    ScoreMoreCountries: number | null;
    ScoreBrand: number | null;
    WeightedResult: number | null;
    ComputedTier: Tier | null;
    ManualOverrideTier: Tier | null;
    FinalTier: Tier | null;
    PreviousYearTier: Tier | null;
    Notes: string | null;
    ModifiedBy: string | null;
    ModifiedAt: string | null;
}

export interface CustomerRow {
    CompanyCode: string;
    CustomerID: string;
    ClassYear: number;
    CustomerName: string | null;
    SalesManagerSnapshot: string | null;
    TotalGp: number;
    ASupplierGp: number;
    ProjectExpectedGp: number;
    PaymentAvgDelay: number | null;
    GpRanking: number;
    ASupplierRanking: number;
    ProjectPoints: number;
    PaymentDelayPoints: number;
    ComputedScore: number;
    ComputedTier: Tier | null;
    ManualOverrideTier: Tier | null;
    FinalTier: Tier | null;
    PreviousYearTier: Tier | null;
    Notes: string | null;
    ModifiedBy: string | null;
    ModifiedAt: string | null;
}

export interface SupplierListResponse { rows: SupplierRow[]; lock: LockStatus; canEdit: boolean; }
export interface CustomerListResponse {
    rows: CustomerRow[]; lock: LockStatus; canEdit: boolean;
    scope: 'sales-manager' | 'country-editor' | 'admin' | 'none';
    appliedSalesManager: string | null;
}

export interface AuditEntry {
    AuditID: number; EntityType: string; CompanyCode: string | null;
    EntityKey: string | null; ClassYear: number | null; ChangedField: string | null;
    OldValue: string | null; NewValue: string | null;
    ChangedBy: string; ChangedAt: string;
}

export interface EditorRow {
    Id: number; UserEmail: string; CompanyCode: string;
    EditorRole: 'BU_Manager' | 'Business_Team_Leader' | 'Sales_Manager';
    IsActive: boolean; CreatedAt: string; CreatedBy: string;
}

export const classificationApi = {
    listYears: () => fetchApi<{ years: YearRow[] }>(`/classification/years`),
    getConfig: (year: number) => fetchApi<ClassificationConfig>(`/classification/config/${year}`),
    updateConfig: (year: number, patch: Partial<Record<string, unknown>>) =>
        fetchApi<ClassificationConfig>(`/classification/config/${year}`, {
            method: 'PUT', body: JSON.stringify(patch),
        }),

    // Suppliers
    recomputeSuppliers: (year: number, company: string) =>
        fetchApi<unknown>(`/classification/suppliers/recompute/${year}/${company}`, { method: 'POST' }),
    listSuppliers: (year: number, company: string) => {
        const qp = new URLSearchParams({ year: String(year), company });
        return fetchApi<SupplierListResponse>(`/classification/suppliers?${qp}`);
    },
    updateSupplier: (year: number, company: string, supplierDimId: string, body: {
        scoreImportant?: number | null; scoreFamilyOwned?: number | null;
        scoreLocalSF?: number | null; scoreMoreCountries?: number | null; scoreBrand?: number | null;
        manualOverrideTier?: Tier | null; notes?: string | null;
    }) => fetchApi<unknown>(
        `/classification/suppliers/${year}/${company}/${encodeURIComponent(supplierDimId)}`,
        { method: 'PUT', body: JSON.stringify(body) }),
    lockSuppliers: (year: number) =>
        fetchApi<unknown>(`/classification/suppliers/lock/${year}`, { method: 'POST' }),

    // Customers
    recomputeCustomers: (year: number, company: string) =>
        fetchApi<unknown>(`/classification/customers/recompute/${year}/${company}`, { method: 'POST' }),
    listCustomers: (year: number, company: string, opts: {
        salesManager?: string; tier?: Tier; onlyChanged?: boolean; onlyOverrides?: boolean; search?: string;
    } = {}) => {
        const qp = new URLSearchParams({ year: String(year), company });
        if (opts.salesManager) qp.set('salesManager', opts.salesManager);
        if (opts.tier) qp.set('tier', opts.tier);
        if (opts.onlyChanged) qp.set('onlyChanged', '1');
        if (opts.onlyOverrides) qp.set('onlyOverrides', '1');
        if (opts.search) qp.set('search', opts.search);
        return fetchApi<CustomerListResponse>(`/classification/customers?${qp}`);
    },
    updateCustomer: (year: number, company: string, customerId: string, body: {
        manualOverrideTier?: Tier | null; notes?: string | null;
    }) => fetchApi<unknown>(
        `/classification/customers/${year}/${company}/${encodeURIComponent(customerId)}`,
        { method: 'PUT', body: JSON.stringify(body) }),
    lockCustomers: (year: number) =>
        fetchApi<unknown>(`/classification/customers/lock/${year}`, { method: 'POST' }),

    // Audit + Editors + Export
    getAudit: (params: { entityType?: string; year?: number; company?: string; entityKey?: string; limit?: number } = {}) => {
        const qp = new URLSearchParams();
        if (params.entityType) qp.set('entityType', params.entityType);
        if (params.year) qp.set('year', String(params.year));
        if (params.company) qp.set('company', params.company);
        if (params.entityKey) qp.set('entityKey', params.entityKey);
        if (params.limit) qp.set('limit', String(params.limit));
        return fetchApi<{ rows: AuditEntry[] }>(`/classification/audit?${qp}`);
    },
    listEditors: () => fetchApi<{ rows: EditorRow[] }>(`/classification/editors`),
    addEditor: (body: { userEmail: string; companyCode: string; editorRole: EditorRow['EditorRole'] }) =>
        fetchApi<unknown>(`/classification/editors`, { method: 'POST', body: JSON.stringify(body) }),
    deleteEditor: (id: number) =>
        fetchApi<void>(`/classification/editors/${id}`, { method: 'DELETE' }),

    /** Returns the URL to download — opens via window.location for streaming. */
    exportUrl: (year: number, type: 'supplier' | 'customer') =>
        `/api/v1/classification/export?year=${year}&type=${type}`,
};
